import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CbreAlertService } from '@cbre/alerts';
import { FaqService } from '@core/services/faq.service';
import { IUser } from '@shared/models/iuser.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject } from 'rxjs';

export interface FAQ {
  id: number;
  pageId: number;
  userId: number;
  question: string;
  answer: string;
  numberOfLikes: number;
  liked?: boolean;
  createBy?: string;
  updateBy?: string;
  expanded?: boolean;
  editMode?: boolean;
}

const DEFAULTS_NEW_FAQ = {
  question: '',
  answer: '',
  numberOfLikes: 0,
  editMode: true,
  id: -1,
};

@Component({
  selector: 'miq-faq-list',
  templateUrl: './faq-list.component.html',
  styleUrls: ['./faq-list.component.scss'],
  standalone: false,
})
export class FaqListComponent implements OnInit, OnDestroy {
  @Input()
  public pageId: number;
  @Input()
  public userId: number;
  @Input()
  public email: string;
  @Input()
  public userInfo: IUser;

  public FAQList = new BehaviorSubject<any>([]);
  public editedFAQList: FAQ[] = [];
  public isEditMode = false;
  public addingFaq = false;
  public newFaq: FAQ;
  public searchString = '';
  public disableSave = false;
  public modifiedList: Map<number, FAQ> = new Map();
  public deletedList: number[] = [];

  constructor(
    private faqService: FaqService,
    private cbreAlertService: CbreAlertService,
    private permissionService: NgxPermissionsService,
    private userInfoResolver: UserInfoResolver
  ) {}

  ngOnInit() {
    this.permissionService.loadPermissions(this.userInfoResolver.resolveGroups());
    this.FAQList.subscribe(results => {
      // clone each object because we dont want to modify the real list until we save
      this.cloneFAQList(results);
    });
    this.getFaqs();
    this.setNewFAQ();
  }

  getFaqs() {
    this.faqService.getFaqs(this.pageId).subscribe(results => this.FAQList.next(results));
  }

  cloneFAQList(faqs) {
    this.editedFAQList = faqs.map(faq => ({ ...faq }));
  }

  toggleExpandAll() {
    this.editedFAQList.forEach(faq => (faq.expanded = !faq.expanded));
  }

  toggleEditMode() {
    this.searchString = '';
    this.editedFAQList.forEach(faq => (faq.editMode = !faq.editMode));
    this.isEditMode = !this.isEditMode;
  }

  updateFaq(faq: FAQ) {
    this.editedFAQList = this.editedFAQList.map(item => (item.id === faq.id ? faq : item));
    this.modifiedList.set(faq.id, faq);
    if (!faq.question || !faq.answer) {
      this.disableSave = true;
    }
  }

  deleteFaq(faq: FAQ) {
    this.editedFAQList = this.editedFAQList.filter(item => item.id !== faq.id);
    this.disableSave = this.editedFAQList.filter(faq => !faq.answer || !faq.question).length !== 0;
    this.deletedList.push(faq.id);
  }

  cancelChanges() {
    this.cloneFAQList(this.FAQList.value);
    this.isEditMode = !this.isEditMode;
    this.addingFaq = false;
  }

  saveChanges() {
    if (this.addingFaq) {
      const validAdd = this.addNewFaqToList();
      if (!validAdd) {
        return false;
      }
      this.addingFaq = false;
    }

    this.toggleEditMode();

    const faqData = {
      modified: Array.from(this.modifiedList.values()),
      deleted: this.deletedList,
    };

    return this.faqService.createFaq(faqData).subscribe(() => {
      this.getFaqs();
      this.cbreAlertService.showInfoSnackBar_l10n('core.saveChanges');
      this.modifiedList.clear();
      this.deletedList = [];
    });
  }

  addFaq() {
    this.setNewFAQ();
    this.addingFaq = true;
  }

  setNewFAQ() {
    this.newFaq = {
      ...DEFAULTS_NEW_FAQ,
      userId: this.userId,
      pageId: this.pageId,
      createBy: this.email,
    };
  }

  updateNewFaq({ question, answer }) {
    this.newFaq = {
      ...this.newFaq,
      question,
      answer,
    };
    this.modifiedList.set(this.newFaq.id, this.newFaq);
  }

  deleteNewFaq() {
    this.addingFaq = false;
    this.modifiedList.delete(DEFAULTS_NEW_FAQ.id);
    this.setNewFAQ();
  }

  addNewFaqToList(): boolean {
    if (this.newFaq.question && this.newFaq.answer) {
      this.editedFAQList.push(this.newFaq);
      this.modifiedList.set(this.newFaq.id, this.newFaq);
      return true;
    }
    return false;
  }

  likeFaq(faqId) {
    const faqToLike = {
      faqId,
      userId: this.userId,
    };

    this.faqService.likeFaq(faqToLike).subscribe(this.getFaqs.bind(this));
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }
}
